import { useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, Minus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Reveal,
  SectionFrame,
  Stagger,
  StaggerItem,
} from '@/components/landing/kit';

/* ------------------------------------------------------------------ *
 * Data model — the honest scoreboard.
 * CadencEZ is 'full' on every row by design; the wedge lives in the
 * rows where competitors are 'none'/'partial'. Competitor cells never
 * animate and never light emerald — the contrast IS the message.
 * ------------------------------------------------------------------ */

type Mark = 'full' | 'partial' | 'none';
type PartialTag = 'quote' | 'addon' | 'generic' | 'limited';

type Cell = { mark: Mark; tag?: PartialTag };

type Row = {
  key: string;
  cadencez: Cell;
  loglass: Cell;
  anaplan: Cell;
  pigment: Cell;
  cube: Cell;
};

const f = (): Cell => ({ mark: 'full' });
const n = (): Cell => ({ mark: 'none' });
const p = (tag: PartialTag): Cell => ({ mark: 'partial', tag });

// Order: cadencez | loglass | anaplan | pigment | cube
const ROWS: Row[] = [
  { key: 'publicPricing', cadencez: f(), loglass: n(), anaplan: n(), pigment: n(), cube: p('quote') },
  { key: 'freeTier', cadencez: f(), loglass: n(), anaplan: n(), pigment: n(), cube: n() },
  { key: 'selfServe', cadencez: f(), loglass: n(), anaplan: n(), pigment: n(), cube: p('quote') },
  { key: 'jpDepth', cadencez: f(), loglass: f(), anaplan: p('limited'), pigment: p('limited'), cube: p('limited') },
  { key: 'aiCitations', cadencez: f(), loglass: n(), anaplan: p('generic'), pigment: p('generic'), cube: n() },
  { key: 'varianceAi', cadencez: f(), loglass: p('addon'), anaplan: p('generic'), pigment: p('generic'), cube: p('generic') },
  { key: 'consolidation', cadencez: f(), loglass: p('limited'), anaplan: f(), pigment: p('addon'), cube: p('addon') },
  { key: 'mgmtParity', cadencez: f(), loglass: f(), anaplan: p('addon'), pigment: p('addon'), cube: p('limited') },
  { key: 'perReportPerms', cadencez: f(), loglass: f(), anaplan: f(), pigment: f(), cube: f() },
  { key: 'publicApi', cadencez: f(), loglass: n(), anaplan: f(), pigment: p('addon'), cube: f() },
];

const COMPETITORS = ['loglass', 'anaplan', 'pigment', 'cube'] as const;
type CompKey = (typeof COMPETITORS)[number];
const COMPETITOR_NAMES: Record<CompKey, string> = {
  loglass: 'Loglass',
  anaplan: 'Anaplan',
  pigment: 'Pigment',
  cube: 'Cube',
};

/* "Where we win" predicate: CadencEZ full AND at least one competitor
 * not full (the wedge rows). Yields 9 of 10 (everything but perReportPerms). */
function isWin(row: Row): boolean {
  if (row.cadencez.mark !== 'full') return false;
  return COMPETITORS.some((c) => row[c].mark !== 'full');
}

/* ------------------------------------------------------------------ */

function MarkCell({
  cell,
  brand = false,
  animateMark = false,
  partialLabel,
  full_sr,
  partial_sr,
  none_sr,
}: {
  cell: Cell;
  brand?: boolean;
  animateMark?: boolean;
  partialLabel?: string;
  full_sr: string;
  partial_sr: string;
  none_sr: string;
}) {
  const reduce = useReducedMotion();
  const chip = 'inline-flex h-6 w-6 items-center justify-center rounded';

  if (cell.mark === 'full') {
    const cls =
      chip +
      (brand
        ? ' bg-emerald-600 text-white'
        : ' bg-emerald-50 text-emerald-700');
    const glyph = (
      <span className={cls}>
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
      </span>
    );
    // Only CadencEZ marks animate in (micro scale 0.7->1) — "our column fills".
    if (animateMark && !reduce) {
      return (
        <>
          <motion.span
            className="inline-flex"
            initial={{ scale: 0.7, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.2, ease: [0.2, 0.6, 0.2, 1] }}
          >
            {glyph}
          </motion.span>
          <span className="sr-only">{full_sr}</span>
        </>
      );
    }
    return (
      <>
        {glyph}
        <span className="sr-only">{full_sr}</span>
      </>
    );
  }

  if (cell.mark === 'partial') {
    return (
      <span className="inline-flex flex-col items-center gap-0.5">
        <span className={chip + ' bg-ink-100 text-ink-500'}>
          <Minus className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
        </span>
        {partialLabel && (
          <span className="num text-[9.5px] leading-none text-ink-400">
            {partialLabel}
          </span>
        )}
        <span className="sr-only">{partial_sr}</span>
      </span>
    );
  }

  return (
    <>
      <span className={chip + ' bg-ink-100 text-ink-400'}>
        <X className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
      </span>
      <span className="sr-only">{none_sr}</span>
    </>
  );
}

export function ComparisonSection({ hideFullLink = false }: { hideFullLink?: boolean } = {}) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'wins'>('all');

  const isJa = i18n.language?.toLowerCase().startsWith('ja');
  const asOfDate = isJa ? '令和8年5月' : '2026-05';

  const visibleRows = useMemo(
    () => (filter === 'wins' ? ROWS.filter(isWin) : ROWS),
    [filter],
  );

  const partialTag = (tag?: PartialTag): string | undefined => {
    if (!tag) return undefined;
    const map: Record<PartialTag, [string, string]> = {
      quote: ['lp.comparison.partialTag.quote', 'quote only'],
      addon: ['lp.comparison.partialTag.addon', 'add-on'],
      generic: ['lp.comparison.partialTag.generic', 'generic LLM'],
      limited: ['lp.comparison.partialTag.limited', 'JP-light'],
    };
    const [key, dv] = map[tag];
    return t(key, { defaultValue: dv });
  };

  const sr = {
    full: t('lp.comparison.mark.full.sr', { defaultValue: 'Fully supported' }),
    partial: t('lp.comparison.mark.partial.sr', { defaultValue: 'Partial or add-on' }),
    none: t('lp.comparison.mark.none.sr', { defaultValue: 'Not available' }),
  };

  const segBase =
    'rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-wide transition-colors';

  return (
    <SectionFrame ambient="none" rule id="comparison" className="py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* HEADER */}
        <Stagger slow className="max-w-3xl mb-8 md:mb-14">
          <StaggerItem>
            <span className="pill-slate inline-block font-mono text-[10.5px] uppercase tracking-[0.22em]">
              {t('lp.comparison.eyebrow', { defaultValue: 'How we compare' })}
            </span>
          </StaggerItem>
          <StaggerItem>
            <h2 className="display serif-jp mt-3 text-3xl md:text-5xl font-medium tracking-tight text-balance text-ink-900">
              {isJa ? (
                <>
                  <span className="relative inline-block">
                    誠実な
                    <Reveal
                      as="span"
                      variant="drawLine"
                      delay={0.15}
                      className="underline-emerald absolute -bottom-1 left-0 right-0 origin-left"
                    />
                  </span>
                  スコアボード ― 全項目、今日この場で検証可能
                </>
              ) : (
                <>
                  An{' '}
                  <span className="relative inline-block">
                    honest
                    <Reveal
                      as="span"
                      variant="drawLine"
                      delay={0.15}
                      className="underline-emerald absolute -bottom-1 left-0 right-0 origin-left"
                    />
                  </span>{' '}
                  scoreboard — every row checkable today
                </>
              )}
            </h2>
          </StaggerItem>
          <StaggerItem>
            <p className="mt-4 text-base md:text-lg text-ink-500 leading-relaxed max-w-2xl">
              {t('lp.comparison.subhead', {
                defaultValue:
                  'No vague leaderboards. Each line below is a capability you can verify in a free workspace or our public docs, scored against the FP&A platforms Japanese teams actually shortlist.',
              })}
            </p>
          </StaggerItem>
        </Stagger>

        {/* FILTER TOGGLE */}
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <div
            role="group"
            aria-label={t('lp.comparison.eyebrow', { defaultValue: 'How we compare' })}
            className="inline-flex items-center gap-1 rounded-full border border-ink-200 p-1"
          >
            <button
              type="button"
              onClick={() => setFilter('all')}
              aria-pressed={filter === 'all'}
              className={
                segBase +
                (filter === 'all' ? ' pill-emerald' : ' text-ink-500 hover:text-ink-700')
              }
            >
              {t('lp.comparison.filter.all', { defaultValue: 'All capabilities' })}
            </button>
            <button
              type="button"
              onClick={() => setFilter('wins')}
              aria-pressed={filter === 'wins'}
              className={
                segBase +
                (filter === 'wins' ? ' pill-emerald' : ' text-ink-500 hover:text-ink-700')
              }
            >
              {t('lp.comparison.filter.wins', { defaultValue: 'Where we win' })}
            </button>
          </div>
          <p className="font-mono text-[11px] text-ink-400 sm:ml-4 sm:order-first sm:mr-auto">
            {t('lp.comparison.filter.caption', {
              defaultValue: 'Showing {{shown}} of {{total}} capabilities',
              shown: visibleRows.length,
              total: ROWS.length,
            })}
          </p>
        </div>

        {/* MATRIX */}
        <div className="overflow-x-auto rounded-lg border border-ink-200 bg-card shadow-hairline">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b border-ink-200 bg-ink-50/70">
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-ink-50/70 px-5 py-3 text-left font-mono text-[10px] uppercase tracking-[0.16em] text-ink-400"
                >
                  {t('lp.comparison.col.capability', { defaultValue: 'Capability' })}
                </th>
                <th
                  scope="col"
                  className="relative z-10 bg-emerald-50/50 px-4 py-3 text-center align-top ring-1 ring-emerald-600/40 shadow-soft"
                >
                  <span className="block font-semibold text-ink-900">CadencEZ</span>
                  <span className="pill-emerald mt-1 inline-block text-[9.5px] uppercase tracking-wide">
                    {t('lp.comparison.col.cadencezTag', {
                      defaultValue: 'Public · self-serve',
                    })}
                  </span>
                </th>
                {COMPETITORS.map((c) => (
                  <th
                    key={c}
                    scope="col"
                    className="px-4 py-3 text-center font-medium text-ink-400"
                  >
                    {COMPETITOR_NAMES[c]}
                  </th>
                ))}
              </tr>
            </thead>

            <Stagger as="tbody" className="divide-y divide-ink-200">
              <AnimatePresence initial={false} mode="popLayout">
                {visibleRows.map((row) => (
                  <StaggerItem
                    as="tr"
                    key={row.key}
                    layout
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.2, 0.6, 0.2, 1] }}
                    className="group hover:bg-ink-50/40 transition-colors"
                  >
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-card px-5 py-3 text-left align-top group-hover:bg-ink-50/40"
                    >
                      <span className="block font-medium text-ink-900 text-[13.5px]">
                        {t(`lp.comparison.row.${row.key}.label`, {
                          defaultValue: ROW_FALLBACK[row.key].label,
                        })}
                      </span>
                      <span className="mt-0.5 block font-mono text-[11px] text-ink-400">
                        {t(`lp.comparison.row.${row.key}.detail`, {
                          defaultValue: ROW_FALLBACK[row.key].detail,
                        })}
                      </span>
                    </th>
                    <td className="bg-emerald-50/40 px-4 py-3 text-center">
                      <MarkCell
                        cell={row.cadencez}
                        brand
                        animateMark
                        partialLabel={partialTag(row.cadencez.tag)}
                        full_sr={sr.full}
                        partial_sr={sr.partial}
                        none_sr={sr.none}
                      />
                    </td>
                    {COMPETITORS.map((c) => (
                      <td key={c} className="px-4 py-3 text-center">
                        <MarkCell
                          cell={row[c]}
                          partialLabel={partialTag(row[c].tag)}
                          full_sr={sr.full}
                          partial_sr={sr.partial}
                          none_sr={sr.none}
                        />
                      </td>
                    ))}
                  </StaggerItem>
                ))}
              </AnimatePresence>
            </Stagger>
          </table>
        </div>

        {/* FOOTER */}
        <div className="mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="font-mono text-xs text-ink-400">
            {t('lp.comparison.updatedNote', {
              defaultValue:
                'Scored {{date}} · corrections welcome at compare@cadencez.io',
              date: asOfDate,
            })}
          </p>
          {!hideFullLink && (
            <Link
              to="/compare"
              className="text-sm font-semibold text-emerald-700 hover:underline"
            >
              {t('lp.comparison.fullLink', {
                defaultValue: 'See the full feature comparison',
              })}
            </Link>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

/* English fallbacks for row labels/details (rendered when no JSON key). */
const ROW_FALLBACK: Record<string, { label: string; detail: string }> = {
  publicPricing: {
    label: 'Public pricing, no sales call',
    detail: 'Live Stripe price catalog · billed per company',
  },
  freeTier: {
    label: 'Genuine free tier',
    detail: '10 seats · 3 sources · dashboard, variance, forecast, Q&A',
  },
  selfServe: {
    label: 'Self-serve onboarding',
    detail: 'Sign up → first variance report in under 15 minutes',
  },
  jpDepth: {
    label: 'Japan-native depth, shipped',
    detail: '試算表 presets · 給与台帳 import · 稟議 routing · 和暦 dates',
  },
  aiCitations: {
    label: 'Audit-grade AI citations',
    detail: 'Every insight cites the exact fact rows — guarded against hallucination',
  },
  varianceAi: {
    label: 'Variance explain-AI on any cell',
    detail: 'Click a number → plain-language driver breakdown',
  },
  consolidation: {
    label: 'Multi-country consolidation',
    detail: 'Per-account FX (avg/period-end/historical) · auto-CTA · IC elimination',
  },
  mgmtParity: {
    label: 'Management-accounting parity',
    detail: '費用配賦 · 編成プロジェクト · immutable versioning · 組織変更 restatement',
  },
  perReportPerms: {
    label: 'Per-report permissions',
    detail: 'Fine-grained view rights down to the individual report',
  },
  publicApi: {
    label: 'Public API + Excel add-in',
    detail: 'Documented REST API · pull figures straight into spreadsheets',
  },
};

export default ComparisonSection;
